/**
 * Keeps the planner state on the phone. Same shape as the event log: a stable snapshot for
 * React's useSyncExternalStore, storage wrapped so a full or blocked disk never breaks planning.
 */
import { emptyPlanner, type PlannerState } from "./plannerState";

export interface PlannerStore {
  get(): PlannerState;
  update(change: (s: PlannerState) => PlannerState): void;
  subscribe(listener: () => void): () => void;
}

export function createPlannerStore(storageKey: string, initial: () => PlannerState): PlannerStore {
  let state: PlannerState;
  try {
    const raw = localStorage.getItem(storageKey);
    state = raw ? { ...emptyPlanner, ...(JSON.parse(raw) as PlannerState) } : initial();
  } catch {
    state = initial();
  }
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    update(change) {
      const next = change(state);
      if (next === state) return;
      state = next;
      try {
        localStorage.setItem(storageKey, JSON.stringify(state));
      } catch {
        // Kept in memory for this session; nothing else to do.
      }
      listeners.forEach((l) => l());
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
